import { Button, ClickEvent, DataHeader, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, DropdownItem, Label, Panel, SlideoutDecorator, TableContentsChangedEvent, TableRow, TableRowMoveEvent } from "@mcleod/components";
import { AutogenLayoutTieredStopChargesTable } from "./autogen/AutogenLayoutTieredStopChargesTable";
import { TieredStopCharge } from "./TieredStopCharge";
import { Alignment, CurrencySettings, HorizontalAlignment, StringUtil } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";
import { DuplicateId, DuplicateIdProps } from "./DuplicateId";

const viewTableURL = "lme/dispatch/TieredStopChargesTable?mode=update&key=";

const DUPLICATE_VALIDATION_PROPS: DuplicateIdProps = {
    tableName: "tiered_charge_hdr",
    screenName: "Tiered Stop Charges Table",
    altFieldNameForDupCheck: "table_id",
    navigateToUrl: viewTableURL
};

export class TieredStopChargesTable extends AutogenLayoutTieredStopChargesTable {

    private isBothType = row => StringUtil.equalsIgnoreCase("B", row.data.getString("charge_type"));

    onLoad(): void | Promise<void> {
        this.tableTieredCharges.onEditLayoutLoaded = (decorator, tableRow) => {
            decorator.multiButton.dropdownItems = null;
            const isSearch = this.mainDataSource.mode === DataSourceMode.SEARCH;
            decorator.multiButton.caption = `Apply ${isSearch ? 'Filters' : 'Edits'}`;
            decorator.labelTitle.text = `${isSearch ? 'Search Tiered Stop Charges' : 'Edit Tiered Stop Charge'} `;
        }

        this.mainDataSource.doBeforeDataSourcePost = async () => {
            if (this.tableTieredCharges.allRows.some(this.isBothType)
                && !this.tableTieredCharges.allRows.every(this.isBothType)) {
                CommonDialogs.showError("All charges must be of type 'Both' if any charge is of type 'Both'.", "Invalid Data");
                return false;
            }
            return true;
        }

        this.tableTieredCharges.addContentsChangedListener((event: TableContentsChangedEvent) => this.syncSequenceByType());
        
        DuplicateId.configureIdTextbox(this.textboxTableId, DUPLICATE_VALIDATION_PROPS);

        
        const mcEnabled = CurrencySettings.getSingleton().multiCurrencyEnabled();
        this.textboxCurrencyType.visible = mcEnabled;
    }

    tableTieredChargesAfterTableRowMove(event: TableRowMoveEvent) {
        this.syncSequenceByType();
    }

    sourceTieredChargeAfterExecution(event: DataSourceExecutionEvent) {
        this.tableTieredCharges.applySort("sequence", "asc", false);
        this.tableTieredCharges.clearSort();
    }

    public override doAfterDataHeaderSetup(dataHeader: DataHeader): void {
        dataHeader.showDelete = true;
    }

    sourceTieredChargeTableAfterModeChange(event: DataSourceModeChangeEvent) {
        const isSearch = event.dataSource.mode === DataSourceMode.SEARCH;
        this.buttonAdd.visible = !isSearch;
        this.buttonSearch.visible = isSearch;
    }

    buttonSearchOnClick(event: ClickEvent) {
        this.tableTieredCharges.allRows[0].showEditLayout();
    }

    buttonAddOnClick(event: ClickEvent) {
        const saveButton = this.createSaveButton();
        SlideoutDecorator.decorateAndSlideIn('lme/dispatch/TieredStopCharge', {
            fillVerticalSpace: true,
            width: "20%",
            minWidth: 440,
            title: 'Add Tiered Stop Charge Detail',
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            transitionOptions: { speed: 200, direction: Alignment.RIGHT },
            addlComponents: saveButton,
            layoutLoadListeners: async (event) => {
                const layout = event.target as TieredStopCharge;
                const tieredStopCharge = await layout.mainDataSource.createBlankRow();
                layout.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [tieredStopCharge]);

                if (this.tableTieredCharges.allRows.length > 0) {
                    const hasBothType = this.tableTieredCharges.allRows.some(this.isBothType);
                    this.syncBothType(layout, hasBothType);
                    if (hasBothType) {
                        tieredStopCharge.set("charge_type", "B");
                        layout.textboxChargeType.enabled = false;
                        layout.textboxChargeCode.focus();
                    }
                }
            },
            doAfterSlideIn: async (decorator: SlideoutDecorator) => {
                saveButton.addClickListener(async event => {
                    saveButton.busy = true;
                    if (decorator.layout.mainDataSource.validate()) {
                        const tieredStopCharge = decorator.layout.activeRow;
                        if (StringUtil.equalsIgnoreCase("B", tieredStopCharge.getString("charge_type"))
                            && this.tableTieredCharges.allRows.some(row => "B" !== row.data.getString("charge_type"))) {
                            const confirmContent = new Panel({ align: HorizontalAlignment.CENTER });
                            confirmContent.add(
                                new Label({ text: "You are adding a charge of type 'Both', but there are existing charges of differing types." }),
                                new Label({ text: "Would you like to convert all charges to 'Both'?" })
                            );
                            const confirmResult = await CommonDialogs.showYesNo(confirmContent, "Confirm 'Both' Charge");
                            if (confirmResult) {
                                this.tableTieredCharges.allRows.forEach(row => row.data.set("charge_type", "B"));
                            } else if (confirmResult !== false) {
                                saveButton.busy = false;
                                return;
                            }
                        }
                        decorator.slideOut();
                        this.tableTieredCharges.addRow(tieredStopCharge, null, { display: true, addToData: true });
                    }
                    saveButton.busy = false;
                });
            }
        });
    }

    createSaveButton() {
        return new Button({
            id: "tieredStopChargeSaveButton",
            caption: "Add Detail",
            backgroundColor: "primary",
            borderWidth: 0,
            rowBreak: false
        });
    }

    tableRowOnDblClick(event: ClickEvent) {
        const tableRow = event.target as TableRow;
        tableRow.showEditLayout();
    }

    syncBothType(layout: TieredStopCharge, hasBothType: boolean) {
        if (hasBothType) {
            layout.textboxChargeType.selectedItem = new DropdownItem("B", "Both");
        } else {
            layout.textboxChargeType.removeItemsWithValue("B");
        }
    }

    syncSequenceByType() {
        this.tableTieredCharges.allRows
        .sort((a, b) => {
            const typeA = a.data.getString("charge_type");
            const typeB = b.data.getString("charge_type");
            const seqA = a.data.getNumber("sequence");
            const seqB = b.data.getNumber("sequence");
            if (typeA === typeB) {
                return seqA - seqB;
            } else {
                return StringUtil.compare(typeA, typeB);
            } 
        }).forEach((row, index) => row.data.set("sequence", index + 1));
        this.tableTieredCharges.applySort("sequence", "asc", false);
        this.tableTieredCharges.clearSort();
    }
}